'use client';

import { User } from 'interfaces';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';

import { PlatformSubscriptionsFacade } from 'lib/platform';
import { MeasurementKey } from 'modules/profiling/measurementTypes';
import { profilingStoreActions } from 'modules/profiling/profilingStore';

import { useAuthStore } from '../';
import { emitUserUpdate } from '../actions/emitUserUpdate';

const addCleanupFunction = (fn: () => void) => {
  useAuthStore.setState(state => ({
    cleanupFunctions: [...(state.cleanupFunctions ?? []), fn]
  }));
};

const setupPlatformSubscriptionListeners = () => {
  let isOnline = window.navigator.onLine;
  let timer: number | undefined = undefined;
  const abortController = new AbortController();
  const scheduleFetch = () => {
    window.clearTimeout(timer);
    if (abortController.signal.aborted) return;

    timer = window.setTimeout(async () => {
      if (abortController.signal.aborted) return;
      if (!isOnline) return scheduleFetch();

      const subscriptionAndEntitlements: Pick<User, 'entitlements' | 'subscription'> =
        await PlatformSubscriptionsFacade.singleton.getSubscriptionAndEntitlements();
      if (abortController.signal.aborted) return;

      useAuthStore.setState(state => {
        if (!state.user) return state;

        const userSubscriptionFields = pick(state.user, ['entitlements', 'subscription']);
        if (isEqual(userSubscriptionFields, subscriptionAndEntitlements)) return state;

        return {
          user: state.user.update(subscriptionAndEntitlements)
        };
      });
      emitUserUpdate();

      scheduleFetch();
    }, 60_000); // 1 minute refetch
  };

  scheduleFetch();

  const handleOnline = () => (isOnline = true);
  const handleOffline = () => (isOnline = false);

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  addCleanupFunction(() => {
    window.clearTimeout(timer);
    abortController.abort();
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  });
};

export async function getSubscriptionAndEntitlements() {
  return profilingStoreActions.measureAction(() => PlatformSubscriptionsFacade.singleton.getSubscriptionAndEntitlements(), {
    measurementKey: MeasurementKey.getSubscriptionAndEntitlements,
    identifier: {},
    context: {}
  });
}

export async function initSubscriptionAndEntitlements() {
  const subscriptionAndEntitlements = await getSubscriptionAndEntitlements();
  setupPlatformSubscriptionListeners();

  return subscriptionAndEntitlements;
}
